<script setup lang="ts">
    import JigsawQuestions from './JigsawQuestions.vue';
    import { ref, reactive } from "vue";
    import { toFixedPrice } from '@/composables/commonFunctions'
    import { useRiskStore } from '@/stores/riskData'
    import { resolveQuoteAddonFallbackImage, resolveQuoteAddonImage } from '../config/productRegistry'

    const props = defineProps<{
        groupAddon: any
        index: number
        productOutput: any
        action: string
    }>();

    const store = useRiskStore()

    const emit = defineEmits<{ (e: 'select', id: any, addonType: 'grouped' | 'single'): void }>()
    const selectedAddon = (id: string) => {
        emit('select', id, 'grouped');
    };

    const groupedAddonMultiSelection = false;

    const groupedAddonsRules = (id: string) => {
        if (!groupedAddonMultiSelection && (props.productOutput.ReturnSchemes
            .find((x: any) => x.IsSelected)
            ?.GroupedAddons
            ?.find((x: any) => x.ProductId == id)
            ?.GroupAddons
            ?.some((x: any) => x.IsSelected) ?? false)) {
            return true;
        }
        return false;
    };
    // Prefer the addon's own ProductId, falling back to the store's product id.
    const addonProductId = (addon: any) => addon?.ProductId ?? store.productId

    const addonImageContext = (addon: any) => ({ productId: addonProductId(addon), variant: 'grouped' as const })

    // Image srcs that failed to load; addonImage() returns the product's fallback for
    // these. A reactive Set so recording a failure re-renders the <img> with the
    // fallback src — keeping Vue's :src binding authoritative (rather than mutating
    // img.src imperatively) avoids a render → re-error loop.
    const failedAddonImages = reactive(new Set<string>())

    const realImage = ref(false)
    const addonImage = (addon: any) => {
        const productId = addonProductId(addon)
        const configured = resolveQuoteAddonImage(productId, addon, addonImageContext(addon))
        if (configured) {
            return failedAddonImages.has(configured)
                ? resolveQuoteAddonFallbackImage(productId, addon, addonImageContext(addon)) ?? configured
                : configured
        }

        return realImage.value
            ? `https://isistech.blob.core.windows.net/cdn/resources/CETA/addons-images/${addon.Code}.png`
            : 'https://isistech.blob.core.windows.net/cdn/resources/CETA/scheme-images/STRATEGIC_FI.png'
    }

    // The browser "checks" existence by attempting to load the image; on failure we
    // record the src so addonImage() switches to the product's fallback on the next
    // render. Only configured (data-derived) images can legitimately 404, so only
    // those are tracked.
    const onAddonImageError = (addon: any) => {
        const productId = addonProductId(addon)
        const configured = resolveQuoteAddonImage(productId, addon, addonImageContext(addon))
        if (!configured) return
        failedAddonImages.add(configured)
    }

    // Documents for a grouped addon come from ProductOutput.AddOnDocuments, keyed by addon Id.
    // Each entry's Value is a list of { Key: document name, Value: document link }.
    const addonDocuments = (addon: any) => {
        return props.productOutput?.AddOnDocuments
            ?.find((entry: any) => entry.Key === addon.Id)
            ?.Value ?? [];
    }

</script>

<template>
    <div class="card-shadow card rounded-lg mb-4 overflow-hidden"
         :class="{ 'border-warning' : addon.IsReferred }"
         v-for="addon in groupAddon.GroupAddons"
         :key="addon.Code">
        <div class="card-body p-0">
            <div class="row no-gutters border-bottom">
                <!-- Left: addon image + name + code -->
                <div class="col-12 col-xl-2 border-right d-flex align-items-center justify-content-center">
                    <div class="w-100 text-center px-3 py-4">
                        <div class="d-flex align-items-center justify-content-center mx-auto mb-3"
                             style="width: 80px; height: 80px;">
                            <img loading="lazy"
                                 class="img-fluid"
                                 :src="addonImage(addon)"
                                 :alt="addon.CustomerFacingName"
                                 @error="onAddonImageError(addon)" />
                        </div>

                        <div class="font-weight-bold text-uppercase text-dark small">
                            {{ addon.Name }}
                        </div>
                    </div>
                </div>

                <!-- Middle: cover details + expandable description/documents -->
                <div class="col-12 col-xl-8">
                    <div class="row p-15 h-100">
                        <div class="col-12 d-flex pl-lg-2">

                            <div class="border p-15" :class="addonDocuments(addon).length ? 'rounded-left w-50' : 'rounded w-100'">
                                <div class="small text-uppercase text-muted font-weight-bold mb-3">
                                    <em class="far fa-info-circle mr-2 text-info"></em>
                                    Cover Details
                                </div>

                                <div class="d-flex pb-1">
                                    <span v-if="addon.Description" class="text-muted fs-12" v-html="addon.Description"></span>
                                    <span v-else class="text-muted fs-12">No cover details available.</span>
                                </div>
                            </div>

                            <!-- Documents -->
                            <div v-if="addonDocuments(addon).length" class="border rounded-right w-50 p-15">
                                <div class="small text-uppercase text-muted font-weight-bold mb-3">
                                    <em class="far fa-download mr-2 text-info"></em>
                                    Documents
                                </div>

                                <div class="d-flex flex-column">
                                    <a v-for="(doc, docIndex) in addonDocuments(addon)"
                                       :key="docIndex"
                                       :href="doc.Value"
                                       target="_blank"
                                       rel="noopener"
                                       class="text-muted fs-12 mb-1">
                                        <em class="far fa-file-alt mr-2"></em>
                                        {{ doc.Key }}
                                    </a>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Commented out for now as it requires a form to be created and its not very relevant to the new scheme as its all based around cars -->
                    <!-- <JigsawQuestions v-if="groupAddon.name === 'Jigsaw' && groupAddon.isSelected" :groupAddon="groupAddon" /> -->
                </div>

                <!-- Right: price + selection -->
                <div class="col-12 col-xl-2 border-left d-flex align-items-center justify-content-center">
                    <div class="w-100 text-right px-3 py-4">
                        <div>
                            <span class="h4 text-muted mr-1">£</span>
                            <span class="display-4 font-weight-bold text-dark" style="font-size: 2.3rem;">
                                {{ toFixedPrice(addon.OriginalTotalPayable).replace('£', '') }}
                            </span>
                        </div>

                        <div class="small text-muted mb-3">per year</div>

                        <input type="checkbox"
                               class="d-none"
                               :checked="addon.IsSelected"
                               :name="`${addon.Code}-groupedAddonSelection[${index}]`"
                               :id="`${addon.Code}_groupedAddonSelection_${index}`"
                               :disabled="(!addon.IsSelected && groupedAddonsRules(addon.ProductId)) || action.toLowerCase().includes('mta')"
                               @change="selectedAddon(addon.Code)" />

                        <button v-if="!addon.IsSelected"
                                type="button"
                                class="btn btn-primary btn-block font-weight-bold py-2 mb-2"
                                :disabled="(!addon.IsSelected && groupedAddonsRules(addon.ProductId)) || action.toLowerCase().includes('mta')"
                                @click="selectedAddon(addon.Code)">
                            <em class="far fa-check-circle mr-2"></em>
                            Add to Cover
                        </button>
                        <button v-else
                                type="button"
                                class="btn btn-danger btn-block font-weight-bold py-2 mb-2"
                                :disabled="action.toLowerCase().includes('mta')"
                                @click="selectedAddon(addon.Code)">
                            <em class="far fa-times-circle mr-2"></em>
                            Remove
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>
